import { createRoot } from 'react-dom/client';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import RootWrapper from '../RootWrapper';
import PageWrapper, { HydrateFallback } from '../PageWrapper';

// Pages
import NotFoundPage from '../pages/NotFoundPage';
import StartPage from '../pages/StartPage';
import ServicesPage from '../pages/ServicesPage';
import TimeSlotsPage from '../pages/TimeSlotsPage';
import ConfirmPage from '../pages/ConfirmPage';
import ContactPage from '../pages/ContactPage';
import QuestionsPage from '../pages/QuestionsPage';
import QuestionsConfirmedPage from '../pages/QuestionsConfirmedPage';
import ServiceUnavailablePage from '../pages/ServiceUnavailablePage';
import FollowUpTimeSlotsPage from '../pages/FollowUpTimeSlotsPage';
import FollowUpContactPage from '../pages/FollowUpContactPage';
import FollowUpConfirmPage from '../pages/FollowUpConfirmPage';
// Loaders
import pageWrapperLoader from './loaders/pageWrapperLoader';
import operationPageLoader from './loaders/operationPageLoader';
import startPageLoader from './loaders/startPageLoader';
import servicesPageLoader from './loaders/servicesPageLoader';
import timeSlotsPageLoader from './loaders/timeSlotsPageLoader';
import questionsPageLoader from './loaders/questionsPageLoader';
import confirmPageLoader from './loaders/confirmPageLoader';
import contactPageLoader from './loaders/contactPageLoader';
import followUpConfirmPageLoader from './loaders/followUpConfirmPageLoader';
import followUpContactPageLoader from './loaders/followUpContactPageLoader';
import followUpTimeSlotsPageLoader from './loaders/followUpTimeSlotsPageLoader';
// Middleware
import analyticsMiddleware from './middleware/analyticsMiddleware';
import serviceUnavailableMiddleware from './middleware/serviceUnavailableMiddleware';
import createAppMiddleware from './middleware/appMiddleware';
import authMiddleware from './middleware/authMiddleware';
import createOperationValidationMiddleware from './middleware/operationValidationMiddleware';
import operationSessionMiddleware, {
  resetSessionMiddleware,
  clearSessionMiddleware,
} from './middleware/operationSessionMiddleware';
import { isFeatureEnabled } from '../helpers/featureHelper';

const router = createBrowserRouter(
  [
    {
      path: '/',
      ErrorBoundary: NotFoundPage,
      element: <PageWrapper view="main" />,
      middleware: [
        createAppMiddleware('main'),
        serviceUnavailableMiddleware,
        analyticsMiddleware,
      ],
      loader: pageWrapperLoader,
      hydrateFallbackElement: <HydrateFallback view="main" />,
      children: [
        {
          index: true,
          element: <NotFoundPage />,
        },
        ...(isFeatureEnabled('followUpBooking')
          ? [
              {
                path: 'followup/timeslots/',
                element: <FollowUpTimeSlotsPage />,
                loader: followUpTimeSlotsPageLoader,
              },
              {
                path: 'followup/contact/',
                element: <FollowUpContactPage />,
                loader: followUpContactPageLoader,
              },
              {
                path: 'followup/confirm/',
                element: <FollowUpConfirmPage />,
                loader: followUpConfirmPageLoader,
              },
            ]
          : []),
        {
          path: ':operation/',
          loader: operationPageLoader,
          middleware: [
            createOperationValidationMiddleware(),
            operationSessionMiddleware,
            authMiddleware,
          ],
          children: [
            {
              index: true,
              element: <StartPage />,
              loader: startPageLoader,
            },
            {
              path: 'services/',
              element: <ServicesPage />,
              loader: servicesPageLoader,
            },
            {
              path: 'timeslots/',
              element: <TimeSlotsPage />,
              loader: timeSlotsPageLoader,
            },
            {
              path: 'contact/',
              element: <ContactPage />,
              loader: contactPageLoader,
            },
            {
              path: 'confirm/',
              element: <ConfirmPage />,
              loader: confirmPageLoader,
            },
          ],
        },
        {
          path: 'questions/',
          element: <QuestionsPage />,
          loader: questionsPageLoader,
          middleware: [clearSessionMiddleware],
        },
        {
          path: 'questions-confirmed/',
          element: <QuestionsConfirmedPage />,
          middleware: [clearSessionMiddleware],
        },
        {
          path: 'service-unavailable/',
          element: <ServiceUnavailablePage />,
          middleware: [resetSessionMiddleware],
        },
        {
          path: '404/',
          element: <NotFoundPage />,
          middleware: [resetSessionMiddleware],
        },
      ],
    },
  ],
  {
    basename: `/${CSS_CONFIG.country}/${CSS_CONFIG.language}/appointment${SUB_PATH}`,
  },
);

createRoot(document.getElementById('root')!).render(
  <RootWrapper view="main">
    <RouterProvider router={router} />
  </RootWrapper>,
);
